import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { genderOptions } from '../constants/gender';
import { lifestyleOptions } from '../constants/lifestyle';
import './RoomsExpandedFilters.css';

export const initialFoundFlatExpandedFilters = {
  gender: [],
  priceRange: { min: '', max: '' },
  desiredTeamSize: [],
  lifestyle: [],
  openToOtherFlat: [],
};

const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 30000;
const PRICE_SLIDER_STEP = 100;

// Common team sizes for "desired team size"
const teamSizeOptions = [
  { value: '2', ar: '٢', en: '2' },
  { value: '3', ar: '٣', en: '3' },
  { value: '4', ar: '٤', en: '4' },
  { value: '5', ar: '٥+', en: '5+' },
];

const openToOtherFlatOptions = [
  { value: 'yes', ar: 'منفتح', en: 'Open to other' },
  { value: 'no', ar: 'غير منفتح', en: 'Not open' },
];

function FoundFlatExpandedFilters({
  isOpen,
  onClose,
  currentLanguage,
  getLocalizedText,
  filters,
  onApply,
}) {
  const [localFilters, setLocalFilters] = useState({
    ...initialFoundFlatExpandedFilters,
    ...filters,
    priceRange: {
      ...initialFoundFlatExpandedFilters.priceRange,
      ...(filters?.priceRange || {}),
    },
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        ...initialFoundFlatExpandedFilters,
        ...filters,
        priceRange: {
          ...initialFoundFlatExpandedFilters.priceRange,
          ...(filters?.priceRange || {}),
        },
      });
    }
  }, [isOpen, filters]);

  const sliderMin = useMemo(() => {
    const v = parseFloat(localFilters.priceRange?.min);
    return Number.isFinite(v)
      ? Math.min(Math.max(v, PRICE_SLIDER_MIN), PRICE_SLIDER_MAX)
      : PRICE_SLIDER_MIN;
  }, [localFilters.priceRange?.min]);

  const sliderMax = useMemo(() => {
    const v = parseFloat(localFilters.priceRange?.max);
    return Number.isFinite(v)
      ? Math.min(Math.max(v, PRICE_SLIDER_MIN), PRICE_SLIDER_MAX)
      : PRICE_SLIDER_MAX;
  }, [localFilters.priceRange?.max]);

  if (!isOpen) return null;

  const toggleArrayFilter = (name, value) => {
    setLocalFilters((prev) => {
      const arr = prev[name] || [];
      const isSelected = arr.includes(value);
      return {
        ...prev,
        [name]: isSelected ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  const setRadioFilter = (name, value) => {
    setLocalFilters((prev) => {
      const arr = prev[name] || [];
      const isSelected = arr.includes(value);
      return {
        ...prev,
        [name]: isSelected ? [] : [value],
      };
    });
  };

  const setPriceField = (field, value) => {
    setLocalFilters((prev) => {
      let min = field === 'min' ? value : prev.priceRange?.min ?? '';
      let max = field === 'max' ? value : prev.priceRange?.max ?? '';

      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      if (Number.isFinite(minNum) && Number.isFinite(maxNum) && minNum > maxNum) {
        if (field === 'min') max = String(minNum);
        else min = String(maxNum);
      }

      return {
        ...prev,
        priceRange: {
          min: min === '' || min == null ? '' : String(min),
          max: max === '' || max == null ? '' : String(max),
        },
      };
    });
  };

  const handleSliderMin = (e) => {
    const val = Number(e.target.value);
    setPriceField('min', Math.min(val, sliderMax));
  };

  const handleSliderMax = (e) => {
    const val = Number(e.target.value);
    setPriceField('max', Math.max(val, sliderMin));
  };


  // Each selected value counts as 1 (min + max = 2, each lifestyle chip = 1, …)
  const countActiveFilters = (f) => {
    if (!f) return 0;
    let n = 0;
    n += (f.gender || []).length;
    if (f.priceRange?.min !== '' && f.priceRange?.min != null) n += 1;
    if (f.priceRange?.max !== '' && f.priceRange?.max != null) n += 1;
    n += (f.desiredTeamSize || []).length;
    n += (f.openToOtherFlat || []).length;
    n += (f.lifestyle || []).length;
    return n;
  };

  const activeFilterCount = countActiveFilters(localFilters);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(initialFoundFlatExpandedFilters);
    onApply(initialFoundFlatExpandedFilters);
  };

  const rangePercentLeft =
    ((sliderMin - PRICE_SLIDER_MIN) / (PRICE_SLIDER_MAX - PRICE_SLIDER_MIN)) * 100;
  const rangePercentRight =
    ((sliderMax - PRICE_SLIDER_MIN) / (PRICE_SLIDER_MAX - PRICE_SLIDER_MIN)) * 100;

  return createPortal(
    <div
      className="ref-overlay"
      onClick={onClose}
      dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
    >
      <aside className="ref-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ref-header">
          <h2>{getLocalizedText('الفلاتر', 'Filters')}</h2>
          <button type="button" className="ref-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="ref-content">
          {/* Gender */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('النوع', 'Gender')}</h3>
            <div className="ref-segmented" role="radiogroup">
              {(genderOptions || []).map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.gender || []).includes(option.value)}
                  className={`ref-segment${
                    (localFilters.gender || []).includes(option.value) ? ' is-selected' : ''
                  }`}
                  onClick={() => setRadioFilter('gender', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Monthly rent */}
          <section className="ref-section">
            <h3 className="ref-section-title">
              {getLocalizedText('إيجار الشقة الشهري', 'Monthly flat rent')}
            </h3>
            <p className="ref-section-sub">
              {getLocalizedText('بالجنيه المصري', 'In Egyptian pounds')}
            </p>

            <div className="ref-slider-wrap">
              <div className="ref-slider-track">
                <div
                  className="ref-slider-range"
                  style={{
                    left: `${rangePercentLeft}%`,
                    width: `${rangePercentRight - rangePercentLeft}%`,
                  }}
                />
              </div>
              <input
                type="range"
                className="ref-slider ref-slider-min"
                min={PRICE_SLIDER_MIN}
                max={PRICE_SLIDER_MAX}
                step={PRICE_SLIDER_STEP}
                value={sliderMin}
                onChange={handleSliderMin}
                aria-label={getLocalizedText('الحد الأدنى', 'Minimum')}
              />
              <input
                type="range"
                className="ref-slider ref-slider-max"
                min={PRICE_SLIDER_MIN}
                max={PRICE_SLIDER_MAX}
                step={PRICE_SLIDER_STEP}
                value={sliderMax}
                onChange={handleSliderMax}
                aria-label={getLocalizedText('الحد الأقصى', 'Maximum')}
              />
            </div>

            <div className="ref-price-inputs">
              <label className="ref-price-field">
                <span>{getLocalizedText('الحد الأدنى', 'Minimum')}</span>
                <div className="ref-price-input-wrap">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={localFilters.priceRange?.min ?? ''}
                    placeholder={String(PRICE_SLIDER_MIN)}
                    onChange={(e) => setPriceField('min', e.target.value)}
                  />
                </div>
              </label>
              <span className="ref-price-dash">—</span>
              <label className="ref-price-field">
                <span>{getLocalizedText('الحد الأقصى', 'Maximum')}</span>
                <div className="ref-price-input-wrap">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={localFilters.priceRange?.max ?? ''}
                    placeholder={`${PRICE_SLIDER_MAX}+`}
                    onChange={(e) => setPriceField('max', e.target.value)}
                  />
                </div>
              </label>
            </div>
          </section>

          {/* Desired team size */}
          <section className="ref-section">
            <h3 className="ref-section-title">
              {getLocalizedText('اقصى حجم للفريق', 'Maximum team size')}
            </h3>
            <div className="ref-segmented" role="radiogroup">
              {teamSizeOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.desiredTeamSize || []).includes(option.value)}
                  className={`ref-segment${
                    (localFilters.desiredTeamSize || []).includes(option.value)
                      ? ' is-selected'
                      : ''
                  }`}
                  onClick={() => setRadioFilter('desiredTeamSize', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>


          {/* Open to other flat */}
          <section className="ref-section">
            <h3 className="ref-section-title">
              {getLocalizedText('منفتح لشقة أخرى؟', 'Open to another flat?')}
            </h3>
            <div className="ref-segmented" role="radiogroup">
              {openToOtherFlatOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.openToOtherFlat || []).includes(option.value)}
                  className={`ref-segment${
                    (localFilters.openToOtherFlat || []).includes(option.value)
                      ? ' is-selected'
                      : ''
                  }`}
                  onClick={() => setRadioFilter('openToOtherFlat', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Lifestyle — 3-column grid, icon above label */}
          <section className="ref-section">
            <h3 className="ref-section-title">
              {getLocalizedText('نمط الحياة', 'Lifestyle')}
            </h3>
            <div className="ref-lifestyle-grid">
              {(lifestyleOptions || []).map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`ref-lifestyle-item${
                    (localFilters.lifestyle || []).includes(option.value) ? ' is-selected' : ''
                  }`}
                  onClick={() => toggleArrayFilter('lifestyle', option.value)}
                >
                  {option.icon && (
                    <img
                      src={option.icon}
                      alt=""
                      className="ref-lifestyle-icon"
                    />
                  )}
                  <span className="ref-lifestyle-label">
                    {getLocalizedText(option.ar, option.en)}
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="ref-footer">
          <button type="button" className="ref-btn-clear" onClick={handleReset}>
            {getLocalizedText('مسح الكل', 'Clear all')}
          </button>
          <button type="button" className="ref-btn-apply" onClick={handleApply}>
            {activeFilterCount > 0
              ? getLocalizedText(`عرض النتائج (${activeFilterCount})`, `Show results (${activeFilterCount})`)
              : getLocalizedText('عرض النتائج', 'Show results')}
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
}

export default FoundFlatExpandedFilters;
